import { useState } from 'react';
import { useI18n } from '../../i18n';
import { IconSliders, IconChevronLeft } from '../ui/icons';
import type { TranslationKey } from '../../i18n/translations';
import { tableauBrandLabel } from './TableauElectriqueModal';

type ToolboxCategory = 'compteurs' | 'tableaux' | 'zones' | 'points';
type TFn = (key: TranslationKey, vars?: Record<string, string | number>) => string;
type TpFn = (base: string, n: number) => string;

interface ToolboxItem {
  id: string;
  type: 'compteur' | 'tableau' | 'zone' | 'point';
  icon: string;
  /** Libellé interne (français). Pour un tableau, c'est le nom enregistré en base et relu par préfixe : ne pas traduire. */
  label: string;
  category: ToolboxCategory;
  /** Libellé et description affichés (traduits). */
  text: (t: TFn, tp: TpFn) => { label: string; description: string };
  // Propriétés spécifiques aux tableaux
  modules?: number;
  rangees?: number;
  marque?: string;
  ref?: string;
}

// Description d'un tableau : « 13 modules • Schneider Resi9 »
const panelDesc = (t: TFn, modules: number, marque: string) =>
  t('arch2_modules_brand', { n: modules, brand: tableauBrandLabel(marque, t) });

const TOOLBOX_ITEMS: ToolboxItem[] = [
  // Compteurs
  { id: 'linky-mono', type: 'compteur', icon: '📊', label: 'Linky Monophasé', category: 'compteurs', text: (t) => ({ label: t('arch2_linky_single'), description: '230V - 12kVA' }) },
  { id: 'linky-tri', type: 'compteur', icon: '📊', label: 'Linky Triphasé', category: 'compteurs', text: (t) => ({ label: t('arch2_linky_three'), description: '400V - 36kVA' }) },
  { id: 'compteur-prod', type: 'compteur', icon: '🔆', label: 'Compteur Production', category: 'compteurs', text: (t) => ({ label: t('arch2_meter_production'), description: t('arch2_photovoltaic') }) },

  // Tableaux - Schneider Resi9
  { id: '1rang-13mod', type: 'tableau', icon: '🔲', label: 'Tableau 1 rangée', category: 'tableaux', text: (t, tp) => ({ label: tp('arch2_panel_rows', 1), description: panelDesc(t, 13, 'Schneider Resi9') }), modules: 13, rangees: 1, marque: 'Schneider Resi9', ref: 'R9H13401' },
  { id: '2rang-26mod', type: 'tableau', icon: '🔲', label: 'Tableau 2 rangées', category: 'tableaux', text: (t, tp) => ({ label: tp('arch2_panel_rows', 2), description: panelDesc(t, 26, 'Schneider Resi9') }), modules: 26, rangees: 2, marque: 'Schneider Resi9', ref: 'R9H13402' },
  { id: '3rang-39mod', type: 'tableau', icon: '🔲', label: 'Tableau 3 rangées', category: 'tableaux', text: (t, tp) => ({ label: tp('arch2_panel_rows', 3), description: panelDesc(t, 39, 'Schneider Resi9') }), modules: 39, rangees: 3, marque: 'Schneider Resi9', ref: 'R9H13403' },
  { id: '4rang-52mod', type: 'tableau', icon: '🔲', label: 'Tableau 4 rangées', category: 'tableaux', text: (t, tp) => ({ label: tp('arch2_panel_rows', 4), description: panelDesc(t, 52, 'Schneider Resi9') }), modules: 52, rangees: 4, marque: 'Schneider Resi9', ref: 'R9H13404' },

  // Tableaux - Legrand Drivia
  { id: 'legrand-2rang', type: 'tableau', icon: '🔳', label: 'Drivia 2 rangées', category: 'tableaux', text: (t) => ({ label: t('arch2_brand_rows', { brand: 'Drivia', n: 2 }), description: panelDesc(t, 26, 'Legrand Drivia') }), modules: 26, rangees: 2, marque: 'Legrand Drivia', ref: '401212' },
  { id: 'legrand-3rang', type: 'tableau', icon: '🔳', label: 'Drivia 3 rangées', category: 'tableaux', text: (t) => ({ label: t('arch2_brand_rows', { brand: 'Drivia', n: 3 }), description: panelDesc(t, 39, 'Legrand Drivia') }), modules: 39, rangees: 3, marque: 'Legrand Drivia', ref: '401213' },
  { id: 'legrand-4rang', type: 'tableau', icon: '🔳', label: 'Drivia 4 rangées', category: 'tableaux', text: (t) => ({ label: t('arch2_brand_rows', { brand: 'Drivia', n: 4 }), description: panelDesc(t, 52, 'Legrand Drivia') }), modules: 52, rangees: 4, marque: 'Legrand Drivia', ref: '401214' },

  // Tableaux - Hager Gamma
  { id: 'hager-2rang', type: 'tableau', icon: '�', label: 'Gamma 2 rangées', category: 'tableaux', text: (t) => ({ label: t('arch2_brand_rows', { brand: 'Gamma', n: 2 }), description: panelDesc(t, 26, 'Hager Gamma') }), modules: 26, rangees: 2, marque: 'Hager Gamma', ref: 'GD213A' },
  { id: 'hager-3rang', type: 'tableau', icon: '🔷', label: 'Gamma 3 rangées', category: 'tableaux', text: (t) => ({ label: t('arch2_brand_rows', { brand: 'Gamma', n: 3 }), description: panelDesc(t, 39, 'Hager Gamma') }), modules: 39, rangees: 3, marque: 'Hager Gamma', ref: 'GD313A' },

  // Tableaux spéciaux
  { id: 'tab-1disj', type: 'tableau', icon: '🔹', label: 'Tableau 1 disjoncteur', category: 'tableaux', text: (t) => ({ label: t('arch2_panel_1breaker'), description: panelDesc(t, 6, 'Modulaire') }), modules: 6, rangees: 1, marque: 'Modulaire', ref: 'TAB-1D' },
  { id: 'sous-tableau', type: 'tableau', icon: '📦', label: 'Sous-tableau', category: 'tableaux', text: (t) => ({ label: t('arch2_subpanel'), description: panelDesc(t, 13, 'Modulaire') }), modules: 13, rangees: 1, marque: 'Modulaire', ref: 'STD-13' },
  { id: 'coffret-ext', type: 'tableau', icon: '🏠', label: 'Coffret extérieur', category: 'tableaux', text: (t) => ({ label: t('arch2_outdoor_box'), description: panelDesc(t, 8, 'Étanche IP65') }), modules: 8, rangees: 1, marque: 'Étanche IP65', ref: 'EXT-IP65' },

  // Zones Wattel
  { id: 'zone-bureau', type: 'zone', icon: '💼', label: 'Zone Bureau', category: 'zones', text: (t) => ({ label: t('arch2_zone_office'), description: t('arch2_desc_offices') }) },
  { id: 'zone-eclairage', type: 'zone', icon: '💡', label: 'Zone Éclairage', category: 'zones', text: (t) => ({ label: t('arch2_zone_lighting'), description: t('arch2_desc_general_lighting') }) },
  { id: 'zone-cvc', type: 'zone', icon: '❄️', label: 'Zone CVC', category: 'zones', text: (t) => ({ label: t('arch2_zone_hvac'), description: t('arch2_desc_hvac') }) },
  { id: 'zone-cuisine', type: 'zone', icon: '🍳', label: 'Zone Cuisine', category: 'zones', text: (t) => ({ label: t('arch2_zone_kitchen'), description: t('arch2_desc_appliances') }) },
  { id: 'zone-serveur', type: 'zone', icon: '💻', label: 'Zone Serveur', category: 'zones', text: (t) => ({ label: t('arch2_zone_server'), description: t('arch2_desc_it') }) },

  // Points électriques
  { id: 'prise-16a', type: 'point', icon: '🔌', label: 'Prise 16A', category: 'points', text: (t) => ({ label: t('arch2_socket_16a'), description: t('arch2_desc_socket_std') }) },
  { id: 'prise-32a', type: 'point', icon: '⚡', label: 'Prise 32A', category: 'points', text: (t) => ({ label: t('arch2_socket_32a'), description: t('arch2_desc_socket_heavy') }) },
  { id: 'eclairage', type: 'point', icon: '💡', label: 'Point lumineux', category: 'points', text: (t) => ({ label: t('arch2_light_point'), description: t('arch2_desc_lighting') }) },
  { id: 'va-et-vient', type: 'point', icon: '🔘', label: 'Va-et-vient', category: 'points', text: (t) => ({ label: t('arch2_two_way'), description: t('arch2_desc_double_switch') }) },
  { id: 'four', type: 'point', icon: '🔥', label: 'Four', category: 'points', text: (t) => ({ label: t('arch2_oven'), description: t('arch2_desc_dedicated_20a') }) },
  { id: 'plaques', type: 'point', icon: '🍳', label: 'Plaques cuisson', category: 'points', text: (t) => ({ label: t('arch2_hob'), description: t('arch2_desc_dedicated_32a') }) },
];

const CATEGORY_KEYS: Record<ToolboxCategory | 'all', TranslationKey> = {
  all: 'arch2_all',
  compteurs: 'arch2_tb_cat_meters',
  tableaux: 'arch2_tb_cat_panels',
  zones: 'arch2_tb_cat_zones',
  points: 'arch2_tb_cat_points',
};

interface ElectricalToolboxProps {
  onAddElement: (type: string, data: any) => void;
  isOpen: boolean;
  onToggle: () => void;
}

export function ElectricalToolbox({ onAddElement, isOpen, onToggle }: ElectricalToolboxProps) {
  const { t, tp } = useI18n();
  const [selectedCategory, setSelectedCategory] = useState<ToolboxCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories: (ToolboxCategory | 'all')[] = ['all', 'compteurs', 'tableaux', 'zones', 'points'];

  // Libellés affichés (traduits) pour chaque élément
  const displayItems = TOOLBOX_ITEMS.map(item => ({ item, ...item.text(t, tp) }));

  const filteredItems = displayItems.filter(({ item, label, description }) => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch = label.toLowerCase().includes(q) ||
                         description.toLowerCase().includes(q) ||
                         item.label.toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  // Données transmises au schéma. Le nom d'un tableau est enregistré en base comme nom de zone
  // et reconnu par son préfixe « Tableau » au rechargement : on garde le libellé interne (français).
  const elementData = (item: ToolboxItem, label: string, description: string) => ({
    label: item.type === 'tableau' ? item.label : label,
    icon: item.icon,
    description,
    // Données spécifiques aux tableaux
    modules: item.modules,
    rangees: item.rangees,
    marque: item.marque,
    ref: item.ref,
  });

  const handleDragStart = (e: React.DragEvent, item: ToolboxItem, label: string, description: string) => {
    e.dataTransfer.setData('application/reactflow', JSON.stringify({
      type: item.type,
      data: elementData(item, label, description),
    }));
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className={`
          fixed left-6 top-1/2 -translate-y-1/2 z-40
          bg-white text-zce-navy p-3 rounded-r-xl shadow-lg border border-gray-200
          hover:bg-gray-50
          transition-all duration-300
          ${isOpen ? 'translate-x-80' : 'translate-x-0'}
        `}
        title={isOpen ? t('arch2_tb_close') : t('arch2_tb_open')}
        aria-label={isOpen ? t('arch2_tb_close') : t('arch2_tb_open')}
      >
        <div className="flex flex-col items-center gap-1">
          {isOpen ? <IconChevronLeft className="w-5 h-5" /> : <IconSliders className="w-5 h-5" />}
          {!isOpen && <span className="text-xs font-semibold tracking-wider writing-mode-vertical">{t('arch2_tb_components_vertical')}</span>}
        </div>
      </button>

      {/* Toolbox Panel */}
      <div className={`
        fixed left-0 top-0 bottom-0 w-80 bg-slate-800 border-r border-slate-700 z-30
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        flex flex-col
      `}>
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-700 to-slate-800 p-4 border-b border-slate-600">
          <h2 className="text-white font-semibold text-base flex items-center gap-2">
            <IconSliders className="w-5 h-5" />
            {t('arch2_tb_title')}
          </h2>
          <p className="text-slate-300 text-xs mt-1">{t('arch2_tb_subtitle')}</p>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-slate-700">
          <div className="relative">
            <input
              type="text"
              placeholder={t('arch2_search_item')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-800 text-white px-4 py-2 pl-10 rounded-lg border border-slate-600 focus:border-cyan-500 focus:outline-none text-sm"
            />
            <span className="absolute left-3 top-2.5 text-slate-400">🔍</span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-2.5 text-slate-400 hover:text-white"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex overflow-x-auto p-2 border-b border-slate-700 gap-1 scrollbar-thin scrollbar-thumb-slate-600">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`
                px-3 py-1.5 rounded text-xs font-semibold whitespace-nowrap transition-colors
                ${selectedCategory === cat
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }
              `}
            >
              {t(CATEGORY_KEYS[cat])}
            </button>
          ))}
        </div>

        {/* Items List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2 scrollbar-thin scrollbar-thumb-slate-600">
          {filteredItems.length === 0 ? (
            <div className="text-center text-slate-400 py-8">
              <p className="text-2xl mb-2">🔍</p>
              <p className="text-sm">{t('arch2_tb_no_item')}</p>
            </div>
          ) : (
            filteredItems.map(({ item, label, description }) => (
              <div
                key={item.id}
                draggable
                onDragStart={(e) => handleDragStart(e, item, label, description)}
                className="
                  bg-slate-700/50 hover:bg-slate-700 border border-slate-600 hover:border-blue-500
                  rounded-lg p-3 cursor-move transition-all duration-200
                  hover:shadow-md hover:shadow-blue-500/10
                  active:scale-98
                  group
                "
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-700 rounded flex items-center justify-center text-xl group-hover:scale-105 transition-transform">
                    {item.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium text-sm truncate">{label}</h3>
                    <p className="text-slate-400 text-xs mt-0.5">{description}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-slate-600/50 text-slate-300 text-xs rounded">
                      {t(CATEGORY_KEYS[item.category])}
                    </span>
                  </div>
                  <button
                    onClick={() => onAddElement(item.type, elementData(item, label, description))}
                    className="w-8 h-8 bg-blue-600/80 hover:bg-blue-600 text-white rounded flex items-center justify-center transition-colors shadow-sm"
                    title={t('arch2_tb_add_to_schema')}
                    aria-label={t('arch2_tb_add_to_schema')}
                  >
                    <span className="text-sm">+</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer Help */}
        <div className="p-4 bg-slate-800 border-t border-slate-700">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <p className="text-blue-300 text-xs flex items-start gap-2">
              <span className="text-lg">💡</span>
              <span>
                <strong>{t('arch2_tb_tip')}</strong> {t('arch2_tb_tip_before')} <strong>➕</strong> {t('arch2_tb_tip_after')}
              </span>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
